import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Clock, 
  AlertTriangle,
  TrendingUp,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Info,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { DerivacionesSkeleton } from '../components/ui/skeleton-loaders';
import { InfoTooltip, SectionHeader, DefinitionsPanel, EmptyState } from '../components/ui/info-components';
import { secureExportDownload } from '../utils/secureExport';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache simple para evitar re-fetch innecesarios
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

export default function DerivacionesPorEquipo({ token, selectedPartner = 'global' }) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('ytd');
  const [data, setData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showDefinitions, setShowDefinitions] = useState(false);
  
  // Ref para evitar doble fetch
  const fetchingRef = useRef(false);

  // Generar cache key
  const getCacheKey = useCallback(() => {
    const instancia = selectedPartner !== 'global' ? selectedPartner : '';
    return `derivaciones-${period}-${instancia}`;
  }, [period, selectedPartner]);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    const cacheKey = getCacheKey();
    const cached = cache.get(cacheKey);
    
    // Usar cache si existe y no está expirado
    if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setData(cached.data);
      setLoading(false);
      fetchingRef.current = false;
      return;
    }
    
    setLoading(true);
    const instancia = selectedPartner !== 'global' ? selectedPartner : '';
    const params = `period=${period}${instancia ? `&instancia=${instancia}` : ''}`;
    
    try {
      const res = await fetch(`${API_URL}/api/atencion/derivaciones/por-equipo?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const responseData = await res.json();
        setData(responseData);
        cache.set(cacheKey, { data: responseData, timestamp: Date.now() });
      }
    } catch (error) {
      console.error('Error fetching derivaciones por equipo:', error);
    }
    setLoading(false);
    fetchingRef.current = false;
  }, [token, period, selectedPartner, getCacheKey]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, period, selectedPartner]);

  const toggleRow = (grupo) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(grupo)) {
      newExpanded.delete(grupo);
    } else {
      newExpanded.add(grupo);
    }
    setExpandedRows(newExpanded);
  };

  const handleExportCSV = async (equipo = 'todos') => {
    const instancia = selectedPartner !== 'global' ? selectedPartner : 'todas';
    const equipoParam = equipo === 'todos' ? 'todos' : encodeURIComponent(equipo);
    
    try {
      const url = `${API_URL}/api/atencion/derivaciones/tickets/export?estado=abierto&instancia=${instancia}&equipo=${equipoParam}`;
      await secureExportDownload({ url, filename: `tickets_pendientes_${instancia}.xlsx` });
    } catch (error) {
      if (error.message !== 'cancelado') {
        console.error('Error exporting CSV:', error);
      }
    }
  };

  // Mostrar skeleton mientras carga
  if (loading) {
    return <DerivacionesSkeleton />;
  }

  const resumen = data?.resumen || {};
  const filas = data?.filas || [];
  const metadata = data?.metadata || {};
  const definiciones = metadata?.definiciones || {};

  return (
    <div className="space-y-6">
      {/* Header con descripción */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionHeader 
          title="Derivaciones por Equipo"
          description="Gestión de tickets pendientes por equipo — identifica backlog, estancados y tickets críticos."
          icon={Users}
        />
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600 font-medium">Período:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="period-filter"
            >
              <option value="mtd">Mes Actual</option>
              <option value="ytd">Año 2026</option>
              <option value="last30">Últimos 30 días</option>
              <option value="last90">Últimos 90 días</option>
            </select>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => handleExportCSV('todos')}
            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700"
            title="Descargar todos los tickets abiertos"
          >
            <Download className="w-4 h-4" />
            Descargar Excel
          </Button>
          <button
            onClick={() => fetchData(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualizar datos"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* KPI Cards con tooltips */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-blue-600 font-medium">Tickets Abiertos</p>
              <InfoTooltip text="HISTÓRICO: Todos los tickets abiertos independientemente de fecha de creación. Estados: Nuevo, En gestión, Esperando respuesta, Escalado, Reabierto." />
            </div>
            <p className="text-3xl font-bold text-blue-700">{resumen.total_abiertos?.toLocaleString() || 0}</p>
            <p className="text-xs text-blue-500 mt-1">Pendientes de resolución</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-red-600 font-medium">% SLA Fuera Término</p>
              <InfoTooltip text="Fórmula: Cerrados fuera de SLA ÷ Total cerrados × 100. Usa el campo hs_is_closed_in_time_to_close_sla de HubSpot." />
            </div>
            <p className="text-3xl font-bold text-red-700">{resumen.pct_sla_fuera_termino_global || 0}%</p>
            <p className="text-xs text-red-500 mt-1">{resumen.total_cerrados_fuera_sla || 0} cerrados tarde</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-amber-600 font-medium">Críticos</p>
              <InfoTooltip text="Tickets abiertos con más de 90 días desde creación O que fueron reabiertos." />
            </div>
            <p className="text-3xl font-bold text-amber-700">{resumen.total_criticos || 0}</p>
            <p className="text-xs text-amber-500 mt-1">&gt;90 días o reincidentes</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-purple-600 font-medium">Equipos</p>
              <InfoTooltip text="Equipos de HubSpot (primary_team del owner). Owners sin equipo aparecen como 'Nombre (s/equipo)'." />
            </div>
            <p className="text-3xl font-bold text-purple-700">{resumen.total_equipos || 0}</p>
            <p className="text-xs text-purple-500 mt-1">{resumen.total_owners_sin_equipo || 0} s/equipo</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-orange-600 font-medium">Estancados &gt;60d</p>
              <InfoTooltip text="Tickets abiertos con más de 60 días desde su creación. Los de >90 días se consideran críticos." />
            </div>
            <p className="text-3xl font-bold text-orange-700">{resumen.total_estancados_60d || 0}</p>
            <p className="text-xs text-orange-500 mt-1">{resumen.total_estancados_90d || 0} con &gt;90 días</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla por Equipo */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">Performance por Equipo</CardTitle>
              <InfoTooltip text="Ordenado por cantidad de tickets abiertos (mayor a menor)" />
            </div>
            <button
              onClick={() => setShowDefinitions(!showDefinitions)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Info className="w-4 h-4" />
              {showDefinitions ? 'Ocultar' : 'Ver'} definiciones
            </button>
          </div>
          
          {/* Definiciones desplegables */}
          {showDefinitions && definiciones && Object.keys(definiciones).length > 0 && (
            <DefinitionsPanel definitions={definiciones} className="mt-3" />
          )}
        </CardHeader>
        <CardContent>
          {filas.length === 0 ? (
            <EmptyState 
              title="No hay datos de derivaciones"
              message="No se encontraron tickets para el período y filtros seleccionados."
              icon={Users}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-3 font-medium text-slate-600">Equipo / Owner</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">
                      Abiertos
                      <InfoTooltip text="Tickets en estados: Nuevo, En gestión, Esperando respuesta, Escalado, Reabierto" />
                    </th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">Cerrados</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">
                      % SLA Fuera
                      <InfoTooltip text="Cerrados fuera de SLA ÷ Total cerrados × 100" />
                    </th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">% Resolución</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">
                      T. Medio
                      <InfoTooltip text="Promedio de días entre creación y cierre. '-' si no hay cerrados con fechas válidas." />
                    </th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">&gt;60d</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">&gt;90d</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">Críticos</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">CSV</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((fila, idx) => (
                    <React.Fragment key={fila.grupo}>
                      <tr 
                        className={`border-b hover:bg-slate-50 transition-colors ${!fila.es_equipo ? 'bg-amber-50/50' : ''}`}
                      >
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            {fila.es_equipo && fila.drill_down_agentes && fila.drill_down_agentes.length > 0 && (
                              <button
                                onClick={() => toggleRow(fila.grupo)}
                                className="p-0.5 hover:bg-slate-200 rounded transition-colors"
                              >
                                {expandedRows.has(fila.grupo) ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            <span className={`font-medium ${!fila.es_equipo ? 'text-amber-700' : ''}`}>
                              {fila.grupo}
                            </span>
                            {!fila.es_equipo && (
                              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                                Individual
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="text-right py-3 px-2">
                          <span className={`font-semibold ${fila.abiertos > 50 ? 'text-red-600' : fila.abiertos > 20 ? 'text-amber-600' : 'text-slate-700'}`}>
                            {fila.abiertos}
                          </span>
                        </td>
                        <td className="text-right py-3 px-2 text-slate-600">{fila.cerrados}</td>
                        <td className="text-right py-3 px-2">
                          <span className={`${fila.pct_sla_fuera_termino > 20 ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                            {fila.pct_sla_fuera_termino}%
                          </span>
                        </td>
                        <td className="text-right py-3 px-2">
                          <span className={`${fila.pct_resolucion >= 80 ? 'text-green-600' : 'text-slate-600'}`}>
                            {fila.pct_resolucion}%
                          </span>
                        </td>
                        <td className="text-right py-3 px-2 text-slate-600">
                          {fila.tiempo_medio_resolucion_dias ?? '—'}
                        </td>
                        <td className="text-right py-3 px-2">
                          <span className={`${fila.estancados_60d > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}`}>
                            {fila.estancados_60d}
                          </span>
                        </td>
                        <td className="text-right py-3 px-2">
                          <span className={`${fila.estancados_90d > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                            {fila.estancados_90d}
                          </span>
                        </td>
                        <td className="text-right py-3 px-2">
                          <span className={`${fila.criticos > 0 ? 'text-red-600 font-bold' : 'text-slate-400'}`}>
                            {fila.criticos}
                          </span>
                        </td>
                        <td className="text-center py-3 px-2">
                          <button
                            onClick={() => handleExportCSV(fila.grupo)}
                            className="p-1.5 hover:bg-emerald-100 rounded text-emerald-600 transition-colors"
                            title={`Descargar tickets de ${fila.grupo}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      
                      {/* Drill-down por agente */}
                      {expandedRows.has(fila.grupo) && fila.drill_down_agentes && (
                        fila.drill_down_agentes.map((agente) => (
                          <tr key={`${fila.grupo}-${agente.agente}`} className="bg-slate-50/50 border-b">
                            <td className="py-2 px-3 pl-10 text-slate-600">
                              ↳ {agente.agente}
                            </td>
                            <td className="text-right py-2 px-2 text-slate-500">{agente.abiertos}</td>
                            <td className="text-right py-2 px-2 text-slate-500">{agente.cerrados}</td>
                            <td colSpan={7}></td>
                          </tr>
                        ))
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer con metadata */}
      <div className="text-xs text-slate-500 flex items-center justify-between px-2">
        <div className="flex items-center gap-1">
          <Info className="w-3 h-3" />
          <span>Fuente: {metadata.fuente || 'Sync de HubSpot'}</span>
        </div>
        <div>
          Última actualización: {metadata.ultima_actualizacion 
            ? new Date(metadata.ultima_actualizacion).toLocaleString('es-AR', { 
                day: '2-digit', 
                month: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit' 
              })
            : '—'
          }
        </div>
      </div>
    </div>
  );
}
